import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { throttle } from 'lodash';


const useScrollableViewShadowStyles = makeStyles({ name: 'ScrollableViewShadow' })(theme => ({
  root: {
    position: 'relative',
    '&:after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: '100%',
      height: '30px',
      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.1), transparent)',
      opacity: 0,
      transitionDuration: `${theme.transitions.duration.standard}ms`,
      pointerEvents: 'none',
    }
  },
  rootScrollBottom: {
    '&:after': {
      opacity: 1,
    }
  },
}));


export const useResizeObserver = (element: HTMLElement | null, callback: () => void) => {
  const callbackRef = React.useRef(callback);

  callbackRef.current = callback;

  React.useEffect(() => {
    if (!element) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      callbackRef.current?.();
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ element ]);
};


export const useScrollableViewShadow = (scrollContainer: HTMLElement | null, enabled: boolean) => {
  const { classes: scrollShadowClasses, cx } = useScrollableViewShadowStyles();
  const [ bottomShadowShown, setBottomShadowShown ] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLElement | null>(null);

  scrollContainerRef.current = scrollContainer;

  const handleScrollShadowVisible = React.useMemo(() => {
    if (!enabled) {
      return undefined;
    }

    return throttle(() => {
      if (!scrollContainerRef.current) {
        return;
      }

      const currentScrollPixels = Math.round(scrollContainerRef.current.clientHeight + scrollContainerRef.current.scrollTop);

      if (currentScrollPixels >= scrollContainerRef.current.scrollHeight) {
        setBottomShadowShown(false);
      } else {
        setBottomShadowShown(true);
      }
    }, 300);
  }, [ enabled ]);

  useResizeObserver(scrollContainer, () => {
    handleScrollShadowVisible?.();
  });

  React.useEffect(() => {
    handleScrollShadowVisible?.();
  }, [ scrollContainer ]);


  return {
    scrollContainerClassName: cx(scrollShadowClasses.root, {
      [scrollShadowClasses.rootScrollBottom]: bottomShadowShown,
    }),
    bottomShadowShown,
    handleScrollShadowVisible,
  };
};
